import { useEffect, useRef, useState } from "react";
import { Megaphone } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const VISIBLE = 6, PAUSE_MS = 5500, STEP_MS = 2600;

// Lines in the visitor's language: highlights use REAL numbers read from the mini-site's metiers.json.
const own = (lang, fr, tr) => (lang !== "fr" && tr?.[lang]?.length === fr.length ? tr[lang] : fr);

// Any other official app: honest lines from its description + facts read from the APK manifest.
const genericLines = (m, t, lang) => [
  t("megaG.new", { name: m.name }), ...own(lang, m.lines || [], m.lines_i18n), ...(m.features || []).map((f) => t(`megaG.f.${f}`)),
  m.version && t("megaG.version", { v: m.version }), m.min_android && t("megaG.android", { v: m.min_android }),
  m.compat && t(`plat.${m.compat}`), t("megaG.free"), t("megaG.custom"), t("megaG.domain"),
].filter(Boolean);

const megaLines = (m, t, lang) => {
  if (m.kind === "generic") return genericLines(m, t, lang);
  const hl = [
    m.count && t("mega.trades", { n: m.count }), t("mega.questionnaire"), m.trial_days && t("mega.trial", { n: m.trial_days }),
    t("mega.online"), t("mega.connect"), t("mega.languages"),
    m.field_count > 0 && t("mega.field", { n: m.free_subaccounts || 10 }), m.field_count > 0 && t("mega.subaccounts", { n: m.free_subaccounts || 10 }),
    m.field_count > 0 && t("mega.phone"), m.gps && t("mega.gps"), m.field_count > 0 && t("mega.messaging"),
  ].filter(Boolean);
  const trades = own(lang, m.trades || [], m.trades_i18n);
  const chunks = [];
  for (let i = 0; i < trades.length; i += 4) chunks.push(trades.slice(i, i + 4).join(" · "));
  return [...hl, ...own(lang, m.atouts || [], m.atouts_i18n), ...chunks];
};

// « Méga pré-pub »: 6 lines visible, still ~5.5 s once really seen, then rises slowly line by line, loops; pauses off-screen.
export const MegaScroller = ({ mega, testId = "mega-scroller" }) => {
  const { t, lang } = useI18n();
  const lines = mega ? megaLines(mega, t, lang) : [];
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  const [top, setTop] = useState(0);
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (!ref.current || reduce) return undefined;
    const io = new IntersectionObserver(([e]) => setSeen(e.isIntersecting && document.visibilityState === "visible"), { threshold: 0.5 });
    io.observe(ref.current);
    const vis = () => document.visibilityState !== "visible" && setSeen(false);
    document.addEventListener("visibilitychange", vis);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", vis); };
  }, [reduce, lines.length]);
  useEffect(() => {
    if (!seen || lines.length <= VISIBLE) return undefined;
    const id = setTimeout(() => setTop((x) => (x + 1) % lines.length), top === 0 ? PAUSE_MS : STEP_MS);
    return () => clearTimeout(id);
  }, [seen, top, lines.length]);
  if (!lines.length) return null;
  const shown = reduce || lines.length <= VISIBLE ? lines : Array.from({ length: VISIBLE + 1 }, (_, i) => lines[(top + i) % lines.length]);
  return (
    <section ref={ref} className="card-carbon min-w-0 rounded-2xl border border-[rgba(245,197,66,0.35)] p-4" data-testid={testId}>
      <p className="mb-2 flex items-center gap-2 font-display text-[0.7rem] uppercase tracking-[0.25em] text-[var(--red-bright)]"><Megaphone className="h-4 w-4 shrink-0" /> {t(mega.kind === "generic" ? "megaG.title" : "mega.title")}</p>
      <ul className={`min-w-0 space-y-1.5 ${reduce ? "" : "max-h-[12.6rem] overflow-hidden"}`} aria-live="off">
        {shown.map((l, i) => (
          <li key={`${top}-${i}`} className={`mega-line break-words text-sm text-neutral-100 ${!reduce && i === 0 && top > 0 ? "mega-out" : ""} ${!reduce && i === VISIBLE ? "mega-in" : ""}`}
            data-testid={`${testId}-line`}>
            <span className="me-1.5 text-[var(--gold)]">•</span>{l}
          </li>
        ))}
      </ul>
    </section>
  );
};
